import { Component, OnInit, Input} from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { Observable, Subscription, of } from "rxjs";
import { filter, tap } from 'rxjs/operators';

import { RouterLinkRendererComponent } from '../../../../common/components/ag-grid/router-link-renderer.component';
import { ModalInfoComponent } from "../../../../common/components/ag-grid/modal-info.component";
import { FlowIndex } from "../../../../common/models/flow.dto"
import { FlowInstanceService } from '../../../../flow/services/flow-instance.service';
import { AuthService } from '../../../../services/auth.service';
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { TabComponent } from '../../../../common/components/tabs/tab.component';

@Component({
    selector: 'wt-buyer-widget',
    templateUrl: './wt-buyer-widget.component.html',
    styleUrls: ['./wt-buyer-widget.component.scss'],
    standalone: false
})
export class WellTransferBuyerWidgetComponent implements OnInit {
    @Input() widgetTitle: string;


    subscriptions: Subscription = new Subscription();
    buyerOrgId: string;
    pendingData: FlowIndex[];
    actionNeededData: FlowIndex[];
    incompleteData: FlowIndex[];
    deniedData: FlowIndex[];
    approvedData: FlowIndex[];
	pendingGridOptions: GridOptions;
	pendingGridApi?: GridApi;
	actionNeededGridOptions: GridOptions;
	actionNeededGridApi?: GridApi;
	incompleteGridOptions: GridOptions;
	incompleteGridApi?: GridApi;
	deniedGridOptions: GridOptions;
	deniedGridApi?: GridApi;
	approvedGridOptions: GridOptions;
	approvedGridApi?: GridApi;


    constructor(
        public readonly flowService: FlowInstanceService,
        private formatterService: GridFormatterService,
        public readonly authService: AuthService,
        private router: Router
    ) {

    }

    ngOnInit() {
        let obs$ = this.refreshData("action needed").subscribe(
            () => { console.log(`action needed data refreshed`) },
            (error) => { console.error(error); }
        );
        this.subscriptions.add(obs$);

		this.actionNeededGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.actionNeededColDefs());
		this.actionNeededGridOptions.onGridReady = (params) => { this.actionNeededGridApi = params.api; };
        this.actionNeededGridOptions.headerHeight = 50;
		this.pendingGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.pendingColDefs());
		this.pendingGridOptions.onGridReady = (params) => { this.pendingGridApi = params.api; };
        this.pendingGridOptions.headerHeight = 50;
		this.incompleteGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.incompleteColDefs());
		this.incompleteGridOptions.onGridReady = (params) => { this.incompleteGridApi = params.api; };
        this.incompleteGridOptions.headerHeight = 50;
		this.deniedGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.deniedColDefs());
		this.deniedGridOptions.onGridReady = (params) => { this.deniedGridApi = params.api; };
        this.deniedGridOptions.headerHeight = 50;
		this.approvedGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.approvedColDefs());
		this.approvedGridOptions.onGridReady = (params) => { this.approvedGridApi = params.api; };
        this.approvedGridOptions.headerHeight = 50;

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onTabClicked(tab: TabComponent): void {
        const title = tab.title.toLocaleLowerCase();
        let obs$: Observable<FlowIndex[]> = this.refreshData(title);
        let subs$ = obs$.subscribe({
           next: () => { console.log(`tab ${title} data refreshed`) },
           error: (error) => { console.error(error); }
        });
        this.subscriptions.add(subs$);
    }

    refreshData(tabName: string): Observable<FlowIndex[]> {
        let obs$: Observable<FlowIndex[]> = of([]);
        let queryParams = new Map<string, string>();
        queryParams.set("@transfereeCompanyId", this.authService.User.organizationId);

        switch (tabName.toLocaleLowerCase()) {
            case "action needed":
                if (this.actionNeededGridApi) {
                    this.actionNeededGridApi.setGridOption("rowData", []);
                    this.actionNeededGridApi.setGridOption("loading", true);
                }
                obs$ = this.flowService.queryFlowIndices("WTBuyerActionNeeded", null, queryParams).pipe(tap((results: FlowIndex[]) => {
                    this.actionNeededData = results;
                    if (this.actionNeededGridApi != null) {
                        this.actionNeededGridApi.sizeColumnsToFit();
                        this.actionNeededGridApi.setGridOption("loading", false);
                    }
                }));
                break;
            case "pending":
                if (this.pendingGridApi) {
                    this.pendingGridApi.setGridOption("rowData", []);
                    this.pendingGridApi.setGridOption("loading", true);
                }
                obs$ = this.flowService.queryFlowIndices("WTBuyerPending", null, queryParams).pipe(tap((results: FlowIndex[]) => {
                    this.pendingData = results;
                    if (this.pendingGridApi != null) {
                        this.pendingGridApi.sizeColumnsToFit();
                        this.pendingGridApi.setGridOption("loading", false);
                    }
                }));
                break;
            case "incomplete":
                if (this.incompleteGridApi) {
                    this.incompleteGridApi.setGridOption("rowData", []);
                    this.incompleteGridApi.setGridOption("loading", true);
                }
                obs$ = this.flowService.queryFlowIndices("WTBuyerIncomplete", null, queryParams).pipe(tap((results: FlowIndex[]) => {
                    this.incompleteData = results;
                    if (this.incompleteGridApi) {
                        this.incompleteGridApi.sizeColumnsToFit();
                        this.incompleteGridApi.setGridOption("loading", false);
                    }
                }));
                break;
            case "denied":
                if (this.deniedGridApi) {
                    this.deniedGridApi.setGridOption("rowData", []);
                    this.deniedGridApi.setGridOption("loading", true);
                }
                obs$ = this.flowService.queryFlowIndices("WTBuyerDenied", null, queryParams).pipe(tap((results: FlowIndex[]) => {
                    this.deniedData = results;
                    if (this.deniedGridApi) {
                        this.deniedGridApi.sizeColumnsToFit();
                        this.deniedGridApi.setGridOption("loading", false);
                    }
                }));
                break;
            case "approved":
                if (this.approvedGridApi) {
                    this.approvedGridApi.setGridOption("rowData", []);
                    this.approvedGridApi.setGridOption("loading", true);
                }
                obs$ = this.flowService.queryFlowIndices("WTBuyerApproved", null, queryParams).pipe(tap((results: FlowIndex[]) => {
                    this.approvedData = results;
                    if (this.approvedGridApi) {
                         this.approvedGridApi.sizeColumnsToFit();
                        this.approvedGridApi.setGridOption("loading", false);
                    }
                }));
                break;
        }

        return obs$;
    }

    dateSubmittedColumn = {
        headerClass: "wrapped",
        comparator: dateComparator,
        headerName: "Date Submitted",
        headerTooltip: "Date Submitted",
        field: "createdDate",
        tooltipValueGetter: this.formatterService.formatDate("createdDate"),
        valueGetter: this.formatterService.formatDate("createdDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        maxWidth: 200
    } as ColDef;

    transferorColumn = {
        headerClass: "wrapped",
        headerName: "Transferor",
        headerTooltip: "Transferor",
        field: "transfererCompanyName",
        tooltipField: "transfererCompanyName",
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
    } as ColDef;

    transfereeColumn = {
        headerClass: "wrapped",
        headerName: "Transferee",
        headerTooltip: "Transferee",
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        field: "transfereeCompanyName",
        tooltipField: "transfereeCompanyName",
    } as ColDef;

    statusColumn = {
        headerClass: "wrapped",
        headerName: "Status",
        headerTooltip: "Status",
        field: "state",
        tooltipField: 'state'
    } as ColDef;

    public pendingColDefs() {
        return [
            this.dateSubmittedColumn,
            {
                headerClass: "wrapped",
                headerName: "Application Number",
                headerTooltip: "Application Number",
                field: "flowIdentifier",
                tooltipField: 'flowIdentifier',
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
                maxWidth: 200
            },
            this.transferorColumn,
            this.statusColumn
        ] as ColDef[];
    }
    public actionNeededColDefs() {
        return [
            {
                headerClass: "wrapped",
                comparator: dateComparator,
                headerName: "Date Submitted",
                headerTooltip: "Date Submitted",
                field: "modifiedDate",
                tooltipValueGetter: this.formatterService.formatDate("modifiedDate"),
                valueGetter: this.formatterService.formatDate("modifiedDate"),
                filter: "agDateColumnFilter",
                filterParams: this.formatterService.defaultDateFilterParams,
                maxWidth: 200
            },
            {
                headerClass: "wrapped",
                headerName: "Application Number",
                headerTooltip: "Application Number",
                field: "flowIdentifier",
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
                tooltipField: 'flowIdentifier',
                cellRendererSelector: (params) => {
                    if (params.data.onBehalf) {
                        return {
                            component: ModalInfoComponent,
                            params: {
                                modalText: (data) => { return `Application ${data.flowIdentifier} is a paper submission that must be completed by ODNR.`; },
                                text: (data) => { return data.flowIdentifier; }
                            }
                        }
                    } else {
                        return {
                            component: RouterLinkRendererComponent,
                            params: {
                                textField: 'flowIdentifier',
                                flowIdField: 'flowId'
                            }
                        }
                    }
                },
                maxWidth: 200
            },
            this.transferorColumn,
            this.statusColumn
        ] as ColDef[];
    }

    public incompleteColDefs() {
        return [
            {
                headerClass: "wrapped",
                comparator: dateComparator,
                headerName: "Date Application Incomplete",
                headerTooltip: "Date Application Incomplete",
                field: "ejectedIncomplete",
                tooltipValueGetter: this.formatterService.formatDate("ejectedIncomplete"),
                valueGetter: this.formatterService.formatDate("ejectedIncomplete"),
                filter: "agDateColumnFilter",
                filterParams: this.formatterService.defaultDateFilterParams,
                //maxWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "Application Number",
                headerTooltip: "Application Number",
                tooltipField: 'flowIdentifier',
                field: "flowIdentifier",
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
            },
            this.transferorColumn,
            this.transfereeColumn
        ] as ColDef[];
    }

    public deniedColDefs() {
        return [
            {
                headerClass: "wrapped",
                headerName: "Application Number",
                headerTooltip: "Application Number",
                tooltipField: 'flowIdentifier',
                field: "flowIdentifier",
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
            },
            {
                headerClass: "wrapped",
                comparator: dateComparator,
                headerName: "Date Application Denied",
                headerTooltip: "Date Application Denied",
                field: "rejectedDate",
                tooltipValueGetter: this.formatterService.formatDate("rejectedDate"),
                valueGetter: this.formatterService.formatDate("rejectedDate"),
                filter: "agDateColumnFilter",
                filterParams: this.formatterService.defaultDateFilterParams,
                //maxWidth: 150
            },
            this.transferorColumn,
            this.transfereeColumn
        ] as ColDef[];
    }

    public approvedColDefs() {
        return [
            {
                headerClass: "wrapped",
                headerName: "Application Number",
                headerTooltip: "Application Number",
                tooltipField: 'flowIdentifier',
                field: "flowIdentifier",
                filter: "agTextColumnFilter",
                filterParams: this.formatterService.defaultTextFilterParams,
            },
            {
                headerClass: "wrapped",
                comparator: dateComparator,
                headerName: "Date Application Approved",
                headerTooltip: "Date Application Approved",
                field: "approvedDate",
                tooltipValueGetter: this.formatterService.formatDate("approvedDate"),
                valueGetter: this.formatterService.formatDate("approvedDate"),
                filter: "agDateColumnFilter",
                filterParams: this.formatterService.defaultDateFilterParams,
                minWidth: 200
            },
            this.transferorColumn,
            this.transfereeColumn
        ] as ColDef[];
    }
}
